import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { useI18n } from '@/contexts/I18nContext';
import { useRollCategoriesStatistics } from '@/features/calendar/hooks/useRollCategoriesStatistics';
import BeltIcon from '@/assets/images/icons/belt.png';

export const UserStatistics: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const isOwnProfile = user?.id === userId;
  const { statistics, isLoading, error } = useRollCategoriesStatistics(userId || '');

  const handleBack = () => {
    navigate(-1);
  };

  // Redirect if trying to access another user's statistics
  React.useEffect(() => {
    if (!isLoading && !isOwnProfile && userId) {
      navigate(`/profile/${userId}`);
    }
  }, [isLoading, isOwnProfile, userId, navigate]);

  const categories = [
    {
      id: 'submissions',
      title: t('statistics.submissions'),
      items: [
        {
          label: t('statistics.submissions_completed'),
          count: statistics.submissions_completed,
        },
        {
          label: t('statistics.submissions_received'),
          count: statistics.submissions_received,
        },
      ],
    },
    {
      id: 'guard',
      title: t('statistics.guard'),
      items: [
        {
          label: t('statistics.guards_passed'),
          count: statistics.guards_passed,
        },
        {
          label: t('statistics.had_guard_passed'),
          count: statistics.had_guard_passed,
        },
      ],
    },
    {
      id: 'mount',
      title: t('statistics.mount'),
      items: [
        {
          label: t('statistics.got_mount'),
          count: statistics.got_mount,
        },
        {
          label: t('statistics.got_mounted'),
          count: statistics.got_mounted,
        },
      ],
    },
    {
      id: 'back',
      title: t('statistics.back'),
      items: [
        {
          label: t('statistics.got_back_control'),
          count: statistics.got_back_control,
        },
        {
          label: t('statistics.got_back_taken'),
          count: statistics.got_back_taken,
        },
      ],
    },
  ];

  // Don't render content if not own profile (will redirect via useEffect)
  if (!isOwnProfile) {
    return null;
  }

  if (isLoading) {
    return (
      <>
        <TopNavigation variant="subtle" />
        <div className="min-h-screen bg-[#160C0C] text-white" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
          <div className="pt-16" style={{ width: '100%', maxWidth: '100vw' }}>
            <div className="flex items-center justify-between py-4 border-b border-[#281B1B] box-border" style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
              <button onClick={handleBack} className="p-1 text-white/80 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">{t('profile.statistics')}</h1>
            </div>
            <div style={{ paddingLeft: '20px', paddingRight: '20px' }}>
              <div className="text-center py-8">
                <div className="text-white/60">{t('statistics.loading')}</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopNavigation variant="subtle" />
        <div className="min-h-screen bg-[#160C0C] text-white" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
          <div className="pt-16" style={{ width: '100%', maxWidth: '100vw' }}>
            <div className="flex items-center justify-between py-4 border-b border-[#281B1B] box-border" style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
              <button onClick={handleBack} className="p-1 text-white/80 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">{t('profile.statistics')}</h1>
            </div>
            <div style={{ paddingLeft: '20px', paddingRight: '20px' }}>
              <div className="text-center py-8">
                <div className="text-white/60">{t('statistics.error_loading')}</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <TopNavigation variant="subtle" />
      <div className="min-h-screen bg-[#160C0C] text-white" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
        {/* Header */}
        <div className="pt-16" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="flex items-center justify-between py-4 border-b border-[#281B1B] box-border" style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <button onClick={handleBack} className="p-1 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">{t('profile.statistics')}</h1>
          </div>

          {/* Content */}
          <div className="pt-2 pb-4 box-border" style={{ width: '100%', maxWidth: '100vw', paddingLeft: '20px', paddingRight: '20px' }}>
            {categories.map((category, categoryIndex) => (
              <div key={category.id}>
                <div className="mb-4">
                  <h2 className="text-base font-bold text-white mb-3">{category.title}</h2>
                  <div className="space-y-2">
                    {category.items.map((item, itemIndex) => (
                      <div key={itemIndex}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/80">{item.label}:</span>
                          <span className="text-sm font-semibold text-white">{item.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Separator with belt icon - only show between categories, not after the last one */}
                {categoryIndex < categories.length - 1 && (
                  <div className="relative flex items-center my-4">
                    <div className="flex-grow border-t border-[#332233]" />
                    <img src={BeltIcon} alt="Belt" className="h-4 w-6 mx-2" />
                    <div className="flex-grow border-t border-[#332233]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

